<script setup>
import Dialog from "primevue/dialog";
import { ref, computed, watch } from "vue";
import { useUserStore } from "@/store/user-store";
import AnButton from "@/components/common/AnButton.vue";

const store = useUserStore();

// Props and Emits
const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  mode: {
    type: String,
    default: "ADD", // "ADD" or "WITHDRAW"
    validator: (value) => ["ADD", "WITHDRAW"].includes(value)
  },
  userHoldings: {
    type: Array,
    default: () => []
  },
  selectedInstrument: {
    type: Object,
    default: null
  },
  currentValue: {
    type: Number,
    default: 0
  },
  prefilledData: {
    type: Object,
    default: null
  },
  initialData: {
    type: Object,
    default: null
  }
});
const emit = defineEmits(["update:modelValue", "update:mode"]);

// Form data
const investmentType = ref(null);
const amountInvested = ref("");
const interestRatePercent = ref("");
const instrumentIssuer = ref("");
const maturityDate = ref("");
const transactionDate = ref("");

// Investment type options with mapping
const investmentTypes = [
  { label: 'Public Provident Fund', value: 'Public Provident Fund', id: '1' },
  { label: 'Employee Pension Fund', value: 'Employee Pension Fund', id: '2' },
  { label: 'Short Term FD', value: 'Short Term FD', id: '3' },
  { label: 'Long-Term/Tax-Saving FD', value: 'Long-Term/Tax-Saving FD', id: '4' }
];

// Dynamic investment type list based on mode
const investmentTypeList = computed(() => {
  if (props.mode === "WITHDRAW") {
    console.log("Raw userHoldings:", props.userHoldings);
    const mapped = props.userHoldings
      .filter(holding => (holding.currentValue || 0) > 0) // Filter out zero current value
      .map(holding => ({
        label: holding.investmentType,
        value: `${holding.investmentType}_${holding.itemId}`, // Use unique identifier
        id: holding.itemId.toString(),
        instrumentType: holding.instrumentType,
        currentValue: holding.currentValue || 0,
        amountInvested: holding.amountInvested,
        instrumentIssuer: holding.instrumentIssuer,
        itemId: holding.itemId,
        investedOn: holding.investedOn || holding.dateUpdated || holding.transactionDate,
        // Keep original data for reference
        originalInvestmentType: holding.investmentType
      }));
    console.log("Mapped investmentTypeList (filtered):", mapped);
    // Also log the investedOn values specifically to debug
    mapped.forEach((item, index) => {
      console.log(`Item ${index} investedOn:`, item.investedOn, typeof item.investedOn);
    });
    return mapped;
  }
  return investmentTypes;
});

// Get max withdrawal amount based on instrument type
const maxWithdrawalAmount = computed(() => {
  if (props.mode === "WITHDRAW" && investmentType.value) {
    const selectedInstrument = investmentTypeList.value.find(item => item.value === investmentType.value);
    if (selectedInstrument) {
      // Ensure consistent rounding - use the same Math.floor as displayed
      const currentValue = Math.floor(selectedInstrument.currentValue);
      if (selectedInstrument.instrumentType === 'Retirement') {
        // 80% of current value for retirement instruments
        return Math.floor(currentValue * 0.8);
      } else {
        // Full current value for non-retirement instruments
        return currentValue;
      }
    }
  }
  return 0;
});

// Get selected instrument details
const selectedInstrumentDetails = computed(() => {
  if (props.mode === "WITHDRAW" && investmentType.value) {
    console.log("Looking for investmentType.value:", investmentType.value);
    console.log("Available options:", investmentTypeList.value);
    const found = investmentTypeList.value.find(item => item.value === investmentType.value);
    console.log("Found instrument details:", found);
    return found;
  }
  return null;
});

// Validation and error handling
const errors = ref({});
const isLoading = ref(false);

// Watch for dialog open/close to reset fields
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    // Reset all fields when dialog opens
    investmentType.value = null;
    amountInvested.value = "";
    interestRatePercent.value = "";
    instrumentIssuer.value = "";
    maturityDate.value = "";
    transactionDate.value = getTodayDate();
    errors.value = {};
    isLoading.value = false;

    // Apply prefilled data if available (for ADD mode)
    if (props.mode === "ADD" && (props.prefilledData || props.initialData)) {
      const prefilledData = props.prefilledData || props.initialData;
      console.log('Applying prefilled data:', prefilledData);

      // Set investment type
      if (prefilledData.investmentType) {
        investmentType.value = prefilledData.investmentType;
      }

      // Set amount invested
      if (prefilledData.amountInvested || prefilledData.investedAmount) {
        amountInvested.value = (prefilledData.amountInvested || prefilledData.investedAmount).toString();
      }

      // Set interest rate if provided
      if (prefilledData.interestRatePercent) {
        interestRatePercent.value = prefilledData.interestRatePercent.toString();
      }

      // Set instrument issuer if provided
      if (prefilledData.instrumentIssuer) {
        instrumentIssuer.value = prefilledData.instrumentIssuer;
      }

      // Set transaction date
      if (prefilledData.transactionDate || prefilledData.investedDate) {
        transactionDate.value = prefilledData.transactionDate || prefilledData.investedDate;
      }

      // Set maturity date if provided
      if (prefilledData.maturityDate) {
        maturityDate.value = prefilledData.maturityDate;
      }
    }
  }
});

// Watch for investment type selection to auto-populate withdrawal amount
watch(investmentType, (newValue) => {
  if (props.mode === "WITHDRAW" && newValue) {
    const selectedInstrument = investmentTypeList.value.find(item => item.value === newValue);
    if (selectedInstrument && selectedInstrument.instrumentType !== 'Retirement') {
      // For non-retirement instruments, auto-populate full withdrawal amount (use same rounding as displayed)
      const flooredValue = Math.floor(selectedInstrument.currentValue);
      amountInvested.value = flooredValue.toString();
    } else {
      // For retirement instruments, clear the field for user input
      amountInvested.value = "";
    }
  }
});

// Computed properties
const isMaturityDateRequired = computed(() => {
  if (props.mode === "WITHDRAW") return false;
  return investmentType.value === 'Short Term FD' ||
         investmentType.value === 'Long-Term/Tax-Saving FD';
});

const isFieldsEnabled = computed(() => {
  return investmentType.value !== null;
});

const dialogTitle = computed(() => {
  if (props.mode === "WITHDRAW") {
    return "Withdraw from Fixed Income Investment";
  }
  return "Add Fixed Income Investment";
});

const isRetirementInstrument = computed(() => {
  return selectedInstrumentDetails.value && selectedInstrumentDetails.value.instrumentType === 'Retirement';
});

// Check if full withdrawal is required (non-retirement instruments)
const isFullWithdrawalOnly = computed(() => {
  return selectedInstrumentDetails.value && selectedInstrumentDetails.value.instrumentType !== 'Retirement';
});

// Computed properties for date ranges
const minWithdrawalDate = computed(() => {
  if (props.mode === "WITHDRAW") {
    const minDate = getMinWithdrawalDate();
    console.log("minWithdrawalDate computed:", minDate);
    return minDate;
  }
  return getFiveYearsAgoDate();
});

const maxWithdrawalDate = computed(() => {
  if (props.mode === "WITHDRAW") {
    const maxDate = getMaxWithdrawalDate();
    console.log("maxWithdrawalDate computed:", maxDate);
    return maxDate;
  }
  return getTodayDate();
});

// Get minimum date for withdrawal (must be after invested date)
const getMinWithdrawalDate = () => {
  console.log("getMinWithdrawalDate called");
  console.log("selectedInstrumentDetails.value:", selectedInstrumentDetails.value);

  if (selectedInstrumentDetails.value && selectedInstrumentDetails.value.investedOn) {
    console.log("investedOn found:", selectedInstrumentDetails.value.investedOn, typeof selectedInstrumentDetails.value.investedOn);
    try {
      const investedDate = new Date(selectedInstrumentDetails.value.investedOn);
      console.log("Parsed investedDate:", investedDate);
      console.log("Is valid date?", !isNaN(investedDate.getTime()));

      // Check if the date is valid
      if (!isNaN(investedDate.getTime())) {
        const today = new Date();
        const investedDateOnly = new Date(investedDate.getFullYear(), investedDate.getMonth(), investedDate.getDate());
        const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

        console.log("today:", today);
        console.log("investedDateOnly:", investedDateOnly);
        console.log("todayOnly:", todayOnly);
        console.log("investedDateOnly > todayOnly:", investedDateOnly > todayOnly);
        console.log("investedDate year:", investedDate.getFullYear(), "today year:", today.getFullYear());

        // If investment date is in the future or today, use today as minimum (allow withdrawal from today)
        if (investedDateOnly >= todayOnly) {
          console.log("Investment date is today or in future, using today as minimum");
          const todayStr = getTodayDate();
          console.log("Returning today as min date:", todayStr);
          return todayStr;
        }

        // If investment date is in the past, use day after investment date as minimum
        investedDate.setDate(investedDate.getDate() + 1);
        const minDate = investedDate.toISOString().split('T')[0];
        console.log("Investment date is in past, calculated minDate:", minDate);
        return minDate;
      }
    } catch (error) {
      console.warn('Error parsing invested date:', error);
    }
  }
  // Fallback to 5 years ago if no valid invested date
  const fallbackDate = getFiveYearsAgoDate();
  console.log("Using fallback date:", fallbackDate);
  return fallbackDate;
};

// Get maximum date for withdrawal
const getMaxWithdrawalDate = () => {
  console.log("getMaxWithdrawalDate called");
  console.log("mode:", props.mode);
  console.log("selectedInstrumentDetails.value:", selectedInstrumentDetails.value);

  if (props.mode === "WITHDRAW" && selectedInstrumentDetails.value && selectedInstrumentDetails.value.investedOn) {
    try {
      const investedDate = new Date(selectedInstrumentDetails.value.investedOn);
      const today = new Date();

      console.log("investedDate:", investedDate);
      console.log("today:", today);

      if (!isNaN(investedDate.getTime())) {
        const investedDateOnly = new Date(investedDate.getFullYear(), investedDate.getMonth(), investedDate.getDate());
        const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

        console.log("investedDateOnly:", investedDateOnly);
        console.log("todayOnly:", todayOnly);
        console.log("investedDateOnly > todayOnly:", investedDateOnly > todayOnly);

        // If investment date is today or in the future, allow selection up to investment date (or today if past)
        if (investedDateOnly >= todayOnly) {
          const maxDate = investedDate.toISOString().split('T')[0];
          console.log("Investment is today or in future, max date is investment date:", maxDate);
          return maxDate;
        } else {
          console.log("Investment is in past, using today as max date");
        }
      }
    } catch (error) {
      console.warn('Error parsing invested date for max date:', error);
    }
  }

  // Default: today's date
  const todayDate = getTodayDate();
  console.log("Using today as max date:", todayDate);
  return todayDate;
};// Get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Get date 5 years ago in YYYY-MM-DD format
const getFiveYearsAgoDate = () => {
  const today = new Date();
  const fiveYearsAgo = new Date(today.getFullYear() - 5, today.getMonth(), today.getDate());
  return fiveYearsAgo.toISOString().split('T')[0];
};

// Set default transaction date to today
transactionDate.value = getTodayDate();

// Handle mode switching
const switchMode = (newMode) => {
  emit("update:mode", newMode);
  // Reset form when switching modes
  resetForm();
};

// Reset form function
const resetForm = () => {
  investmentType.value = null;
  amountInvested.value = "";
  interestRatePercent.value = "";
  instrumentIssuer.value = "";
  maturityDate.value = "";
  transactionDate.value = getTodayDate();
  errors.value = {};
  isLoading.value = false;
};

// Validation function
const validateForm = () => {
  errors.value = {};

  if (!investmentType.value) {
    errors.value.investmentType = 'Investment type is required';
  }

  if (props.mode === "WITHDRAW") {
    // Validation for WITHDRAW mode
    if (!amountInvested.value || parseFloat(amountInvested.value) <= 0) {
      errors.value.amountInvested = 'Withdrawal amount must be greater than 0';
    }

    if (parseFloat(amountInvested.value) > maxWithdrawalAmount.value) {
      errors.value.amountInvested = `Cannot withdraw more than ₹${maxWithdrawalAmount.value.toLocaleString()}`;
    }
  } else {
    // Validation for ADD mode
    if (!amountInvested.value || parseFloat(amountInvested.value) < 100) {
      errors.value.amountInvested = 'Amount must be at least ₹100';
    }

    if (parseFloat(amountInvested.value) > 50000000) {
      errors.value.amountInvested = 'Amount cannot exceed ₹5 crores';
    }

    if (!interestRatePercent.value || parseFloat(interestRatePercent.value) <= 0) {
      errors.value.interestRatePercent = 'Interest rate is required and must be greater than 0';
    }

    if (parseFloat(interestRatePercent.value) > 30) {
      errors.value.interestRatePercent = 'Interest rate cannot exceed 30%';
    }

    if (!instrumentIssuer.value || instrumentIssuer.value.trim().length === 0) {
      errors.value.instrumentIssuer = 'Instrument issuer is required';
    }

    if (instrumentIssuer.value && instrumentIssuer.value.length > 30) {
      errors.value.instrumentIssuer = 'Instrument issuer cannot exceed 30 characters';
    }

    if (isMaturityDateRequired.value && !maturityDate.value) {
      errors.value.maturityDate = 'Maturity date is required for this investment type';
    }
  }

  if (!transactionDate.value) {
    errors.value.transactionDate = 'Transaction date is required';
  } else if (props.mode === "WITHDRAW" && selectedInstrumentDetails.value && selectedInstrumentDetails.value.investedOn) {
    try {
      const minDate = getMinWithdrawalDate();
      if (transactionDate.value < minDate) {
        const investedDate = new Date(selectedInstrumentDetails.value.investedOn);
        if (!isNaN(investedDate.getTime())) {
          const formattedInvestedDate = investedDate.toLocaleDateString('en-IN');
          errors.value.transactionDate = `Withdrawal date cannot be before investment date (${formattedInvestedDate})`;
        } else {
          errors.value.transactionDate = 'Withdrawal date must be after investment date';
        }
      }
    } catch (error) {
      console.warn('Error validating withdrawal date:', error);
      // Don't show error for date validation issues, just log
    }
  }

  return Object.keys(errors.value).length === 0;
};

// Add fixed income function
const addFixedIncome = async () => {
  if (!validateForm()) {
    return;
  }

  isLoading.value = true;

  try {
    let payload;

    if (props.mode === "WITHDRAW") {
      // WITHDRAW mode payload
      const selectedDetails = selectedInstrumentDetails.value;
      payload = {
        itemId: selectedDetails.itemId,
        transactionType: "SELL",
        amountInvested: -Math.abs(parseFloat(amountInvested.value)), // Negative for withdrawal
        transactionDate: transactionDate.value
      };
    } else {
      // ADD mode payload
      const selectedType = investmentTypes.find(type => type.value === investmentType.value);
      payload = {
        instrumentId: selectedType.id,
        investmentType: investmentType.value,
        amountInvested: parseFloat(amountInvested.value),
        interestRatePercent: parseFloat(interestRatePercent.value),
        instrumentIssuer: instrumentIssuer.value.trim(),
        maturityDate: maturityDate.value || null,
        transactionDate: transactionDate.value,
        transactionType: "ADD"
      };
    }

    await store.addFixedIncomeData(payload);

    // Close dialog on success
    closeDialog();

  } catch (error) {
    console.error('Error processing fixed income:', error);
    errors.value.submit = `Failed to ${props.mode.toLowerCase()} fixed income. Please try again.`;
  } finally {
    isLoading.value = false;
  }
};

// Close dialog function
const closeDialog = () => {
  emit("update:modelValue", false);
};
</script>

<template>
  <Dialog
    modal
    :visible="modelValue"
    :closable="false"
    :dismissable-mask="false"
    @update:visible="(value) => emit('update:modelValue', value)"
  >
    <template #header>
      <div class="d-flex align-items-center position-relative w-100 p-20 dialog-header">
        <!-- Mode Toggle Buttons - Left Top Corner -->
        <div class="mode-toggle-container-left">
          <AnButton
            label="Add"
            :variant="props.mode === 'ADD' ? 'primary' : 'tab'"
            @click="switchMode('ADD')"
            class="mode-toggle-btn-small"
          />
          <AnButton
            label="Withdraw"
            :variant="props.mode === 'WITHDRAW' ? 'primary' : 'tab'"
            @click="switchMode('WITHDRAW')"
            class="mode-toggle-btn-small"
          />
        </div>

        <span class="text-center text-primary w-100 font-20 font-weight-700">
          {{ dialogTitle }}
        </span>
        <i
          @click="closeDialog"
          class="pi pi-times bg-error text-white p-5 br-5 position-absolute r-0 mr-10 cursor-pointer"
        />
      </div>
    </template>

    <div class="ph-30 pb-30">
      <!-- Step 1: Investment Type Selection -->
      <div class="step-section">
        <div class="step-header">
          <div class="step-indicator">
            <span class="step-number">1</span>
          </div>
          <span class="step-title">Investment Type Selection</span>
        </div>

        <div class="d-grid investment-grid gap-20 mt-15">
          <div class="d-flex flex-direction-column">
            <label for="investmentType" class="font-weight-600">
              {{ props.mode === 'WITHDRAW' ? 'Select Investment to Withdraw From' : 'Investment Type' }}
            </label>
            <select
              id="investmentType"
              v-model="investmentType"
              class="input-field"
              :class="{ 'border-red-500': errors.investmentType, 'placeholder-select': !investmentType }"
            >
              <option value="" disabled selected hidden>
                {{ props.mode === 'WITHDRAW' ? 'Select investment to withdraw from' : 'Select investment type' }}
              </option>
              <option
                v-for="type in investmentTypeList"
                :key="type.id"
                :value="type.value"
              >
                {{ type.originalInvestmentType || type.label }}
                <span v-if="props.mode === 'WITHDRAW'">
                  (Current: ₹{{ Math.floor(type.currentValue || 0).toLocaleString() }})
                </span>
              </option>
            </select>
            <small v-if="errors.investmentType" class="error-msg">{{ errors.investmentType }}</small>
          </div>

          <!-- Show current value and withdrawal limits for WITHDRAW mode -->
          <div v-if="props.mode === 'WITHDRAW' && selectedInstrumentDetails" class="d-flex flex-direction-column">
            <label class="font-weight-600">Current Value & Withdrawal Limits</label>
            <div class="info-box">
              <div class="info-row">
                <span>Current Value:</span>
                <span class="font-weight-600">₹{{ Math.floor(selectedInstrumentDetails.currentValue || 0).toLocaleString() }}</span>
              </div>
              <div class="info-row">
                <span>Max Withdrawal:</span>
                <span class="font-weight-600 text-primary">₹{{ maxWithdrawalAmount.toLocaleString() }}</span>
              </div>
              <div v-if="isRetirementInstrument" class="note-text">
                <i class="pi pi-info-circle text-primary"></i>
                <span>Retirement funds allow partial withdrawal up to 80% of current value</span>
              </div>
              <div v-else class="note-text">
                <i class="pi pi-info-circle text-primary"></i>
                <span>Full withdrawal will close this investment</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 2: Investment Details -->
      <div class="step-section" :class="{ 'disabled-section': !isFieldsEnabled }">
        <div class="step-header">
          <div class="step-indicator" :class="{ 'active': isFieldsEnabled }">
            <span class="step-number">2</span>
          </div>
          <span class="step-title">Investment Details</span>
        </div>

        <div class="d-grid transaction-grid gap-20 mt-15">
          <div class="d-flex flex-direction-column">
            <label for="amountInvested" class="font-weight-600">
              {{ props.mode === 'WITHDRAW' ? 'Withdrawal Amount (₹)' : 'Amount Invested (₹)' }}
            </label>
            <input
              id="amountInvested"
              v-model="amountInvested"
              type="text"
              :disabled="!isFieldsEnabled"
              :readonly="props.mode === 'WITHDRAW' && isFullWithdrawalOnly"
              :placeholder="props.mode === 'WITHDRAW' ?
                `Enter withdrawal amount (max ₹${maxWithdrawalAmount.toLocaleString()})` :
                'Enter amount'"
              class="input-field"
              :class="{
                'border-red-500': errors.amountInvested,
                'readonly-field': props.mode === 'WITHDRAW' && isFullWithdrawalOnly
              }"
              @input="amountInvested = $event.target.value.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1')"
            />
            <small v-if="errors.amountInvested" class="error-msg">{{ errors.amountInvested }}</small>
            <small class="helper-text">
              {{ props.mode === 'WITHDRAW' ?
                (isFullWithdrawalOnly ? 'Full withdrawal required for this investment type' : `Range: ₹1 - ₹${maxWithdrawalAmount.toLocaleString()}`) :
                'Range: ₹100 - ₹5,00,00,000' }}
            </small>
          </div>

          <div v-if="props.mode === 'ADD'" class="d-flex flex-direction-column">
            <label for="interestRate" class="font-weight-600">Interest Rate (%)</label>
            <input
              id="interestRate"
              v-model="interestRatePercent"
              type="text"
              :disabled="!isFieldsEnabled"
              placeholder="Enter interest rate"
              class="input-field"
              :class="{ 'border-red-500': errors.interestRatePercent }"
              @input="interestRatePercent = $event.target.value.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1')"
            />
            <small v-if="errors.interestRatePercent" class="error-msg">{{ errors.interestRatePercent }}</small>
            <small class="helper-text">Range: 0% - 30%</small>
          </div>

          <div v-if="props.mode === 'ADD'" class="d-flex flex-direction-column">
            <label for="instrumentIssuer" class="font-weight-600">Instrument Issuer</label>
            <input
              id="instrumentIssuer"
              v-model="instrumentIssuer"
              type="text"
              maxlength="30"
              :disabled="!isFieldsEnabled"
              placeholder="e.g., HDFC Bank, Government Of India"
              class="input-field"
              :class="{ 'border-red-500': errors.instrumentIssuer }"
            />
            <small v-if="errors.instrumentIssuer" class="error-msg">{{ errors.instrumentIssuer }}</small>
            <small class="helper-text">{{ instrumentIssuer.length }}/30 characters</small>
          </div>

          <div class="d-flex flex-direction-column">
            <label for="transactionDate" class="font-weight-600">
              {{ props.mode === 'WITHDRAW' ? 'Withdrawal Date' : 'Transaction Date' }}
            </label>
            <input
              id="transactionDate"
              v-model="transactionDate"
              type="date"
              :key="`date-picker-${investmentType}`"
              :disabled="!isFieldsEnabled"
              :min="minWithdrawalDate"
              :max="maxWithdrawalDate"
              class="input-field"
              :class="{ 'border-red-500': errors.transactionDate }"
            />
            <small v-if="errors.transactionDate" class="error-msg">{{ errors.transactionDate }}</small>
            <small class="helper-text">
              {{ props.mode === 'WITHDRAW' ?
                (selectedInstrumentDetails && selectedInstrumentDetails.investedOn ?
                  (() => {
                    try {
                      const investedDate = new Date(selectedInstrumentDetails.investedOn);
                      if (!isNaN(investedDate.getTime())) {
                        const today = new Date();
                        const investedDateOnly = new Date(investedDate.getFullYear(), investedDate.getMonth(), investedDate.getDate());
                        const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

                        if (investedDateOnly > todayOnly) {
                          return 'Investment date is in future - withdrawal available from today';
                        } else {
                          return `Must be after investment date (${investedDate.toLocaleDateString('en-IN')})`;
                        }
                      } else {
                        return 'Select withdrawal date';
                      }
                    } catch {
                      return 'Select withdrawal date';
                    }
                  })() :
                  'Select withdrawal date') :
                'Up to 5 years back date allowed' }}
            </small>
          </div>

          <!-- Maturity Date (Conditional for ADD mode only) -->
          <div v-if="isMaturityDateRequired" class="d-flex flex-direction-column">
            <label for="maturityDate" class="font-weight-600">Maturity Date</label>
            <input
              id="maturityDate"
              v-model="maturityDate"
              type="date"
              :disabled="!isFieldsEnabled"
              class="input-field"
              :class="{ 'border-red-500': errors.maturityDate }"
            />
            <small v-if="errors.maturityDate" class="error-msg">{{ errors.maturityDate }}</small>
          </div>
        </div>
      </div>

      <!-- Submit Error -->
      <div v-if="errors.submit" class="error-section">
        <p class="text-red-600 text-sm">{{ errors.submit }}</p>
      </div>
    </div>

    <template #footer>
      <div class="d-flex justify-center gap-20 w-100 p-10">
        <AnButton
          label="Back"
          variant="tab"
          @click="closeDialog"
          :disabled="isLoading"
        />
        <AnButton
          :label="props.mode === 'WITHDRAW' ? 'Withdraw' : 'Add'"
          variant="primary"
          :disabled="!isFieldsEnabled || isLoading"
          @click="addFixedIncome"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
.dialog-header {
  border-bottom: 2px solid;
  border-bottom-color: var(--an-primary);
}

/* Mode Toggle Styling - Left Top Corner */
.mode-toggle-container-left {
  position: absolute;
  left: 20px;
  top: 20px;
  display: flex;
  gap: 8px;
  z-index: 10;
}

.mode-toggle-btn-small {
  min-width: 60px !important;
  font-size: 12px !important;
  padding: 6px 12px !important;
  height: 32px;
}

/* Step Layout Styling */
.step-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  transition: all 0.3s ease;
}

.step-section.disabled-section {
  opacity: 0.6;
  background: #f1f5f9;
}

.step-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.step-indicator {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #cbd5e1;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.step-indicator.active {
  background: var(--an-primary);
}

.step-number {
  font-size: 12px;
}

.step-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

/* Grid Layouts */
.investment-grid {
  grid-template-columns: 1fr;
  max-width: 100%;
}

.transaction-grid {
  grid-template-columns: repeat(2, 1fr);
  max-width: 100%;
}

/* Info Box Styling for WITHDRAW mode */
.info-box {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 12px;
  margin-top: 8px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.info-row:last-of-type {
  margin-bottom: 0;
}

.note-text {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #374151;
  margin-top: 8px;
  padding: 8px;
  background: #fef3c7;
  border-radius: 6px;
  border: 1px solid #f59e0b;
}

.input-field {
  padding: 16px;
  border: 2px solid var(--an-gray1);
  border-radius: 5px;
  font-size: 14px;
  width: 100%;
  max-width: 280px;
}

.input-field:focus {
  outline: none;
  border-color: var(--an-primary);
}

.input-field.border-red-500 {
  border-color: #ef4444;
}

.input-field.placeholder-select {
  color: #9ca3af;
}

.input-field option {
  color: #000000;
}

.readonly-field {
  background-color: #f3f4f6 !important;
  cursor: not-allowed;
  color: #6b7280 !important;
}

.error-msg {
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
}

.helper-text {
  color: #6b7280;
  font-size: 12px;
  margin-top: 4px;
}

.error-section {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px;
  margin-top: 16px;
}

/* Dialog Size Styling */
:deep(.p-dialog) {
  width: 35vw !important;
  max-width: 650px !important;
  min-width: 500px !important;
}

:deep(.p-dialog .p-dialog-content) {
  padding: 0 !important;
}

@media (max-width: 700px) {
  :deep(.p-dialog) {
    width: 98vw !important;
    min-width: 0 !important;
    max-width: 100vw !important;
  }

  .ph-30.pb-30 {
    padding-left: 8px !important;
    padding-right: 8px !important;
    padding-bottom: 8px !important;
  }

  .step-section {
    padding: 12px 6px 12px 6px;
    margin-bottom: 12px;
  }

  .d-grid.investment-grid, .d-grid.transaction-grid {
    display: flex !important;
    flex-direction: column !important;
    gap: 12px !important;
  }

  .input-field {
    font-size: 1rem;
    padding: 12px;
    max-width: 100%;
  }

  .dialog-header span {
    font-size: 1.2rem !important;
  }

  .step-title, .font-weight-600 {
    font-size: 1rem !important;
  }

  .d-flex.justify-center.gap-20.w-100.p-10 {
    flex-direction: column;
    gap: 12px;
    padding: 0 0 12px 0;
  }
}
</style>
